import { useEffect, useState } from 'react';
import {
  IStatementTemplateOptions,
  isValidStatementTemplateOptions,
  MAX_STATEMENT_AGED_BREAKPOINTS,
  MAX_STATEMENT_AGED_BREAKPOINT_DAYS,
  MAX_STATEMENT_SPLIT_ON_DUE_INDEX_DAYS,
} from 'shared';

import { Button } from '../../../../../../common/Atoms/Button';
import { InputTextAddon } from '../../../../../../common/Atoms/InputTextAddon';
import { Toggle } from '../../../../../../common/Atoms/Toggle';
import { Paragraph } from '../../../../../../common/Atoms/Typography/Paragraph';

interface BuilderStatementOptionsProps {
  options: IStatementTemplateOptions;
  busy: boolean;
  onSave: (options: IStatementTemplateOptions) => Promise<void>;
}

// One slot per breakpoint, null when blank
function toSlots(breakpoints: number[]): (number | null)[] {
  return Array.from({ length: MAX_STATEMENT_AGED_BREAKPOINTS }, (_, i) => breakpoints[i] ?? null);
}

export function BuilderStatementOptions({ options, busy, onSave }: BuilderStatementOptionsProps) {
  const [slots, setSlots] = useState(toSlots(options.agedBreakpoints));
  const [splitOnDue, setSplitOnDue] = useState(options.splitOnDue);
  const [splitOnDueIndex, setSplitOnDueIndex] = useState<number | null>(options.splitOnDueIndex);

  const savedKey = JSON.stringify(options);

  // Follow the saved value when the AI, a reset or another template changes it
  useEffect(() => {
    setSlots(toSlots(options.agedBreakpoints));
    setSplitOnDue(options.splitOnDue);
    setSplitOnDueIndex(options.splitOnDueIndex);
  }, [savedKey]);

  const draft = {
    agedBreakpoints: slots.filter((s): s is number => s !== null),
    splitOnDue,
    splitOnDueIndex: splitOnDueIndex ?? 0,
  };
  const valid = isValidStatementTemplateOptions(draft) && (!splitOnDue || splitOnDueIndex !== null);
  const changed = JSON.stringify(draft) !== savedKey;

  return (
    <div className={ `mt-4 border-t border-slate-200 pt-4` }>
      <Paragraph variant={ `help` }>
        { `Aged analysis breakpoints. Clear a field to remove the breakpoint.` }
      </Paragraph>
      <div className={ `grid grid-cols-3 gap-8` }>
        { slots.map((slot, i) => (
          <InputTextAddon
            key={ `agedBreakpoint${i}` }
            dataType={ `number` }
            name={ `agedBreakpoint${i + 1}` }
            description={ `Breakpoint ${i + 1}` }
            value={ slot ?? `` }
            disabled={ busy }
            addOnText={ `days` }
            onChange={ ({ value }) => setSlots(prev => prev.map((p, j) => (j === i ? (value === null || value === `` ? null : Number(value)) : p))) }
          />
        )) }
      </div>
      <div className={ `mt-4` }>
        <Toggle
          label={ `Group statement lines` }
          description={ `Group the statement lines based on the days until due.` }
          name={ `splitOnDue` }
          checked={ splitOnDue }
          disabled={ busy }
          onChange={ () => setSplitOnDue(!splitOnDue) }
        />
        { splitOnDue && (
          <div className={ `mt-2 w-72` }>
            <InputTextAddon
              dataType={ `number` }
              name={ `splitOnDueIndex` }
              description={ `Use 0 to split overdue & outstanding on the due date.` }
              value={ splitOnDueIndex ?? `` }
              disabled={ busy }
              addOnText={ `days overdue` }
              onChange={ ({ value }) => setSplitOnDueIndex(value === null || value === `` ? null : Number(value)) }
            />
          </div>
        ) }
      </div>
      <div className={ `mt-3 flex items-center gap-3` }>
        <Button
          color={ `white` }
          disabled={ busy || !changed || !valid }
          onClick={ () => valid && onSave(draft) }
        >
          { `Save` }
        </Button>
        { changed && !valid && (
          <Paragraph
            size={ `xs` }
            className={ `text-red-600` }
          >
            { `Breakpoints must increase, from 1 to ${MAX_STATEMENT_AGED_BREAKPOINT_DAYS} days. Days overdue must be between -${MAX_STATEMENT_SPLIT_ON_DUE_INDEX_DAYS} and ${MAX_STATEMENT_SPLIT_ON_DUE_INDEX_DAYS}.` }
          </Paragraph>
        ) }
      </div>
      <Paragraph
        size={ `xs` }
        variant={ `secondary` }
        className={ `mt-1` }
      >
        { `These belong to this template. The settings under Statement apply to the standard statement and the portal. Publish to use a change on statements.` }
      </Paragraph>
    </div>
  );
}
